import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { jsPDF as JsPdf } from 'jspdf';
import { saveAs } from 'file-saver-es';
import { Workbook } from 'exceljs';
import DataGrid, {
  Sorting, HeaderFilter, Scrolling, SearchPanel,
  Export, Column, Toolbar, Item, LoadPanel,
  DataGridTypes, Editing, Button, Form,
  Popup, Lookup, FilterRow,
  Grouping, StateStoring
} from 'devextreme-react/data-grid';
import SelectBox from 'devextreme-react/select-box';
import './WorkTaskTable.scss';
import { RequiredRule } from 'devextreme-react/form';
import { Item as FormItem } from 'devextreme-react/form';
import { Button as CustomButton } from 'devextreme-react/button';
import { exportDataGrid as exportDataGridToPdf } from 'devextreme/pdf_exporter';
import { exportDataGrid as exportDataGridToXLSX } from 'devextreme/excel_exporter';
import { EmployeeTagBoxComponent } from '../employee-tag-box/employeeTagBoxComponent';
import { RelationDropDownBoxComponent } from '../relation-dropdown-component/relationDropDownBoxComponent';
import DataSource from 'devextreme/data/data_source';
import CustomStore from 'devextreme/data/custom_store';
import 'devextreme/data/odata/store';
import axios from 'axios';
import moment from 'moment';
import { useAuth } from '../../../contexts/auth';
import { WORK_TASK_STATUS_LIST } from '../../../shared/constants';
import { withLoadPanel } from '../../../utils/withLoadPanel';
import Guid from 'devextreme/core/guid';
import { postCreateInvoice } from '../../../api/workTask';
import notify from 'devextreme/ui/notify';
import { confirm } from 'devextreme/ui/dialog';

const onExporting = (e: DataGridTypes.ExportingEvent) => {
    if (e.format === 'pdf') {
      const doc = new JsPdf();
      exportDataGridToPdf({
        jsPDFDocument: doc,
        component: e.component,
      }).then(() => {
        doc.save('WorkTasks.pdf');
      });
    } else {
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet('WorkTasks');

      exportDataGridToXLSX({
        component: e.component,
        worksheet,
        autoFilterEnabled: true,
      }).then(() => {
        workbook.xlsx.writeBuffer().then((buffer) => {
          saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'WorkTasks.xlsx');
        });
      });
      e.cancel = true;
    }
};

const exportFormats = ['xlsx', 'pdf'];
const Grid = ({ relationOid, backTo }: { relationOid?: string, backTo?: string }) => {
const [gridDataSource, setGridDataSource] = useState<any>([]);
const [activityData, setActivityDataSource] = useState<any>([]);
const [relationData, setRelationDataSource] = useState<any>([]);
const [employeeData, setEmployeeDataSource] = useState<any>([]);
const [availableYears, setAvailableYears] = useState<number[]>([]);
const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
const gridRef = useRef<DataGrid>(null);
const navigate = useNavigate();
const { user, signOut } = useAuth();
const dataSource = new DataSource({
    store: {
        type: 'odata',
        version: 4,
        key: 'Oid',
        keyType: 'Guid',
        url: `${process.env.REACT_APP_URL}/api/odata/WorkTask`,
        errorHandler: (error) => {
            if (error.httpStatus === 401) {
                signOut(true);
            }
        },
        beforeSend: (e) => {
            e.headers = {
                'Authorization': `Bearer ${user?.token}`
            };
            // Ensure all GUID values in filters are properly formatted without quotes
            if (e.params && e.params.$filter) {
                // Remove quotes around GUID values and guid() function calls
                e.params.$filter = e.params.$filter.replace(/guid'([^']+)'/gi, '$1');
                e.params.$filter = e.params.$filter.replace(/'([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})'/gi, '$1');
                // Handle DevExtreme's saved state filter format
                e.params.$filter = e.params.$filter.replace(/\["([^"]+)",\s*"=",\s*"([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})"\]/gi, '($1 eq $2)');
                e.params.$filter = e.params.$filter.replace(/\["([^"]+)",\s*"<>",\s*"([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})"\]/gi, '($1 ne $2)');
            }
        },
        onInserting: (e) => {
          if (e.Activity) {
            e['ActivityOid'] = e.Activity.Oid._value.toString();
            delete e.Activity;
          }

          if (e.Relation) {
            e['RelationOid'] = e.Relation.Oid._value.toString();
            delete e.Relation;
          }

          if (e.Employees) {
            e['EmployeeOids'] = e.Employees.map(employee => employee._value);
            delete e.Employees;
          }

         },
         onUpdating: (e, values) =>
        {
          if (values.Activity) {
            values['ActivityOid'] = values.Activity.Oid._value.toString();
          }
          if (values.Relation) {
            values['RelationOid'] = values.Relation.Oid._value.toString();
          }
          if (values.Employees) {
            values['EmployeeUpdated'] = true;
            if (values.Employees.length == 1 && values.Employees[0].Oid)
            {
              values['EmployeeOids'] = values.Employees.map(employee => employee.Oid._value.toString());
            }
            else
            {
              values['EmployeeOids'] = values.Employees.map(employee => employee._value);
            }
              delete e.Employees;
          }
        }
    },
    select: [
        'Oid',
        'TaskDescription',
        'TaskRemarks',
        'TaskStartDate',
        'TaskEndDate',
        'TaskAllDay',
        'TaskDone',
        'Activity',
        'Status',
        'CreatedBy',
        'Employees',
        'CompletedWorkOrders',
        'HasUsedMaterials',
        'Relation',
        'CreatedAt',
        'UpdatedAt',
    ],
    filter: workTaskFilter(relationOid, selectedYear),
    expand: ['Employees', 'Relation', 'CreatedBy', 'Activity'],
});

  function workTaskFilter(relationOid, year)
  {
      const startOfYear = new Date(year, 0, 1);
      const endOfYear = new Date(year, 11, 31, 23, 59, 59);

      const yearFilter = [
        ['TaskStartDate', '>=', startOfYear],
        'and',
        ['TaskStartDate', '<=', endOfYear]
      ];

      if(relationOid)
      {
          return [
            ['Relation/Oid', '=', new Guid(relationOid)],
            'and',
            yearFilter
          ];
      }
      else
      {
          return [
            ['Relation/Oid', '<>', new Guid('00000000-0000-0000-0000-000000000000')],
            'and',
            yearFilter
          ];
      }
  }

  const activityDataSource = {
    store: {
        type: 'odata',
        key: 'Oid',
        keyType: 'Guid',
        url: `${process.env.REACT_APP_URL}/api/odata/Activity`,
        version: 4,
        beforeSend: function(e) {
          e.headers = {
            'Authorization': `Bearer ${user?.token}`
          };

          // Ensure all GUID values in filters are properly formatted without quotes
          if (e.params && e.params.$filter) {
            e.params.$filter = e.params.$filter.replace(/guid'([^']+)'/gi, '$1');
            e.params.$filter = e.params.$filter.replace(/'([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})'/gi, '$1');
            e.params.$filter = e.params.$filter.replace(/\["([^"]+)",\s*"=",\s*"([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})"\]/gi, '($1 eq $2)');
            e.params.$filter = e.params.$filter.replace(/\["([^"]+)",\s*"<>",\s*"([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})"\]/gi, '($1 ne $2)');
          }
        },
    }
};

const relationDataSource = {
  store: {
      type: 'odata',
      key: 'Oid',
      keyType: 'Guid',
      url: `${process.env.REACT_APP_URL}/api/odata/Relation`,
      version: 4,
      beforeSend: function(e) {
        e.headers = {
          'Authorization': `Bearer ${user?.token}`
        };

        // Ensure all GUID values in filters are properly formatted without quotes
        if (e.params && e.params.$filter) {
          e.params.$filter = e.params.$filter.replace(/guid'([^']+)'/gi, '$1');
          e.params.$filter = e.params.$filter.replace(/'([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})'/gi, '$1');
          e.params.$filter = e.params.$filter.replace(/\["([^"]+)",\s*"=",\s*"([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})"\]/gi, '($1 eq $2)');
          e.params.$filter = e.params.$filter.replace(/\["([^"]+)",\s*"<>",\s*"([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})"\]/gi, '($1 ne $2)');
        }
      },
  }
};

const employeesDataSource = {
  store: {
      type: 'odata',
      key: 'Oid',
      keyType: 'Guid',
      url: `${process.env.REACT_APP_URL}/api/odata/Employee`,
      version: 4,
      beforeSend: function(e) {
        e.headers = {
          'Authorization': `Bearer ${user?.token}`
        };

        // Ensure all GUID values in filters are properly formatted without quotes
        if (e.params && e.params.$filter) {
          e.params.$filter = e.params.$filter.replace(/guid'([^']+)'/gi, '$1');
          e.params.$filter = e.params.$filter.replace(/'([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})'/gi, '$1');
          e.params.$filter = e.params.$filter.replace(/\["([^"]+)",\s*"=",\s*"([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})"\]/gi, '($1 eq $2)');
          e.params.$filter = e.params.$filter.replace(/\["([^"]+)",\s*"<>",\s*"([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})"\]/gi, '($1 ne $2)');
        }
      },
  },
  filter : ['Active', '=', true]
};

useEffect(() => {
  setGridDataSource(dataSource);
  setActivityDataSource(activityDataSource);
  setRelationDataSource(relationDataSource);
  setEmployeeDataSource(employeesDataSource);
}, [selectedYear]);

// Fetch available years from WorkTask data
useEffect(() => {
  const yearStore = new CustomStore({
    loadMode: 'raw',
    load: async() => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_URL}/api/WorkTask/available-years`,
          {
            headers: {
              'Authorization': `Bearer ${user?.token}`
            }
          }
        );

        if (response.ok) {
          return await response.json();
        }
        throw new Error('Failed to fetch years');
      } catch (error) {
        // Fallback: only show current year if fetch fails
        return [new Date().getFullYear()];
      }
    }
  });

  const yearDataSource = new DataSource({
    store: yearStore
  });

  yearDataSource.load().then((years) => {
    setAvailableYears(years);
  });
}, [user?.token]);

// Restore focused row from localStorage after data loads
useEffect(() => {
  const storageKey = `workTaskTableFocusedRow_${user?.tenantName || 'default'}`;
  const savedFocusedRow = localStorage.getItem(storageKey);
  if (savedFocusedRow && gridRef.current && gridDataSource) {
    try {
      const focusedRowKey = JSON.parse(savedFocusedRow);
      const instance = gridRef.current.instance;
      // Wait for data to load before setting focused row
      setTimeout(() => {
        instance.option('focusedRowKey', focusedRowKey);
        localStorage.removeItem(storageKey);
      }, 100);
    } catch (e) {
      void e;
    }
  }
}, [gridDataSource]);

const onAddWorkTaskClick = useCallback(() => {
gridRef.current?.instance.addRow();
}, []);

const onClearFilters = useCallback(() => {
  if (gridRef.current) {
    gridRef.current.instance.clearFilter();
    gridRef.current.instance.option('searchPanel.text', '');
  }
}, []);

const onExportToErp = useCallback((e) => {
      notify('Werkbon wordt verwerkt. Even geduld aub...', 'info', 600);
      postCreateInvoice(e.row.data.Oid._value, user?.token)
      .then(() => {
                notify('Werkbonnen verstuurd naar Exact', 'success', 500);
                gridRef.current?.instance.refresh();
        }).catch((error) =>
          {
            notify(error.response.data, 'error', 1000);
          }
        );
  }, []);

const onRowClick = useCallback(({ data }: DataGridTypes.RowClickEvent) => {
  // Save focused row key to localStorage before navigating
  if (gridRef.current) {
    const instance = gridRef.current.instance;
    const focusedRowKey = instance.option('focusedRowKey');
    const storageKey = `workTaskTableFocusedRow_${user?.tenantName || 'default'}`;
    localStorage.setItem(storageKey, JSON.stringify(focusedRowKey));
  }
  navigate('/worktask-details', { state: { workTaskOid: data.Oid._value.toString(), backTo: backTo, relationOid: relationOid } });
}, [backTo, navigate, relationOid]);

const refresh = useCallback(() => {
gridRef.current?.instance.refresh();
}, []);

const toLocalIso = (date) => {
  if (!date) return undefined;
  const d = date instanceof Date ? date : new Date(date);
  const pad = (n) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const onSaving = useCallback(async(e: DataGridTypes.SavingEvent) => {
  const insertChange = e.changes.find(c => c.type === 'insert');
  const updateChange = e.changes.find(c => c.type === 'update');
  if (insertChange) {
    e.cancel = true;
    const data = insertChange.data;
    const sendEmail = await confirm('Wilt u een e-mailnotificatie naar de klant sturen?', 'E-mail versturen');
    axios.post('/api/WorkTask/Create', {
      TaskDescription: data.TaskDescription,
      TaskRemarks: data.TaskRemarks,
      Status: data.Status ?? 'New',
      TaskStartDate: toLocalIso(data.TaskStartDate),
      TaskEndDate: toLocalIso(data.TaskEndDate),
      ActivityOid: data.Activity?.Oid?._value?.toString(),
      RelationOid: data.Relation?.Oid?._value?.toString(),
      EmployeeOids: data.Employees?.map(emp => emp._value?.toString() ?? emp.toString()),
      SendEmail: sendEmail,
    }, {
      headers: { 'Authorization': `Bearer ${user?.token}` }
    }).then(() => {
      e.component.cancelEditData();
      e.component.refresh();
    }).catch(() => {
      notify('Er is een fout opgetreden bij het opslaan van de opdracht', 'error', 1000);
    });
  } else if (updateChange) {
    e.cancel = true;
    const key = updateChange.key?._value?.toString() ?? updateChange.key?.toString();
    const data = updateChange.data;
    const employeeOids = data.Employees
      ? data.Employees.map(emp => emp.Oid?._value?.toString() ?? emp._value?.toString() ?? emp.toString())
      : undefined;
    const sendEmail = await confirm('Wilt u een e-mailnotificatie naar de klant sturen?', 'E-mail versturen');
    axios.put(`/api/WorkTask/Update/${key}`, {
      TaskDescription: data.TaskDescription,
      TaskRemarks: data.TaskRemarks,
      Status: data.Status,
      TaskStartDate: toLocalIso(data.TaskStartDate),
      TaskEndDate: toLocalIso(data.TaskEndDate),
      ActivityOid: data.Activity?.Oid?._value?.toString(),
      RelationOid: data.Relation?.Oid?._value?.toString(),
      EmployeeOids: employeeOids,
      SendEmail: sendEmail,
    }, {
      headers: { 'Authorization': `Bearer ${user?.token}` }
    }).then(() => {
      e.component.cancelEditData();
      e.component.refresh();
    }).catch(() => {
      notify('Er is een fout opgetreden bij het opslaan van de opdracht', 'error', 1000);
    });
  }
}, [user?.token]);

const cellTemplate = (container: { textContent: any; title: any; }, options) => {
const noBreakSpace = '\u00A0';
const text = options.value.map(item => item.FullName).join(', ');
container.textContent = text || noBreakSpace;
container.title = text;
};

// Group by Month & Year (Sort by MM but display "MMMM YYYY")
const groupByDate = (rowData) => {
  return moment(rowData.TaskStartDate).format('YYYY-MM');
};

// Custom group cell renderer to show "MMMM YYYY"
const groupCellRender = (options) => (
  <div>{moment(options.value, 'YYYY-MM').format('MMMM YYYY')}</div>
);
const editorOptions = { height: 40 };
  return (
    <DataGrid
    className='grid'
    noDataText='Geen data beschikbaar'
    autoNavigateToFocusedRow
    focusedRowEnabled
    keyExpr='Oid'
    height='100%'
    dataSource={gridDataSource}
    onRowClick={onRowClick}
    onExporting={onExporting}
    onSaving={onSaving}
    allowColumnReordering
    ref={gridRef}>
    <LoadPanel showPane />
    <StateStoring
      enabled
      savingTimeout={500}
      type='localStorage'
      storageKey={`workTaskTableState_${user?.tenantName || 'default'}_${relationOid || 'all'}`}
    />
    <Grouping autoExpandAll={false} />
    <Editing
      useIcons
      allowAdding
      allowUpdating
      allowDeleting
      mode='popup'>
  <Form>
    <FormItem itemType='group' colCount={2} colSpan={2}>
      <FormItem editorOptions={editorOptions} dataField='Relation.Oid' colSpan={2} />
      <FormItem editorOptions={editorOptions} editorType='dxSelectBox' dataField='Activity.Oid' />
      <FormItem editorOptions={editorOptions} dataField='Status' editorType='dxSelectBox' />
      <FormItem editorOptions={editorOptions} dataField='TaskStartDate' />
      <FormItem editorOptions={editorOptions} dataField='TaskEndDate' />
      <FormItem editorOptions={editorOptions} dataField='Employees' colSpan={2} />
      <FormItem editorOptions={editorOptions} dataField='TaskDescription' colSpan={2} />
      <FormItem
        dataField='TaskRemarks'
        caption='Omschrijving'
        editorType='dxTextArea'
        colSpan={2}
        editorOptions={{ height: 200 }} />
      </FormItem>
  </Form>
        <Popup
            width={600}
            height={650}
            showTitle
            title='Opdracht' />
      </Editing>
    <SearchPanel width={240} visible placeholder='Opdracht zoeken' />
    <Export allowExportSelectedData formats={exportFormats} />
    <HeaderFilter visible allowSearch />
    <FilterRow visible />
    <Sorting mode='multiple' />
    <Scrolling mode='virtual' />
    <Toolbar>
      <Item location='before'>
        <div className='grid-header'>Opdrachten</div>
      </Item>
      <Item location='after' locateInMenu='never'>
        <SelectBox
          items={availableYears}
          value={selectedYear}
          onValueChanged={(e) => setSelectedYear(e.value)}
          width={100}
          placeholder='Jaar'
        />
      </Item>
      <Item location='after' locateInMenu='auto'>
       <CustomButton
          icon='plus'
          text='Opdracht'
          type='default'
          stylingMode='contained'
          onClick={onAddWorkTaskClick}
        />
      </Item>
      <Item
        location='after'
        locateInMenu='auto'
        showText='inMenu'
        widget='dxButton'>
        <CustomButton
          icon='refresh'
          text='Refresh'
          stylingMode='text'
          onClick={refresh}
        />
      </Item>
      <Item
        location='after'
        locateInMenu='auto'
        showText='inMenu'
        widget='dxButton'>
        <CustomButton
          icon='clearformat'
          text='Filters wissen'
          stylingMode='text'
          onClick={onClearFilters}
        />
      </Item>
      <Item location='after' locateInMenu='auto' />
      <Item name='exportButton' />
      <Item location='after' locateInMenu='auto' />
      <Item name='columnChooserButton' locateInMenu='auto' />
      <Item name='searchPanel' locateInMenu='auto' />
      </Toolbar>
    <Column dataField='TaskDone' alignment='left' caption='V' width={50} />
    <Column dataField='CompletedWorkOrders' caption='Aantal' width={100} />
    <Column visible={false} dataField='TaskRemarks' alignment='left' caption='Omschrijving' />
    <Column dataField='TaskDescription' alignment='left' caption='Opdracht'>
       <RequiredRule message='Opdracht title ontbreekt!' />
    </Column>
    <Column
      editCellComponent={RelationDropDownBoxComponent}
      dataField='Relation.Oid'
      caption='Relatie'>
        <Lookup dataSource={relationData} valueExpr='Oid' displayExpr='Name' />
        <RequiredRule message='Selecteer een klant!' />
    </Column>
    <Column dataField='Activity.Oid' caption='Werkzaamheden'>
        <Lookup dataSource={activityData} valueExpr='Oid' displayExpr='Name' />
        <RequiredRule message='Selecteer een bewerking!' />
    </Column>
    <Column visible={false} dataField='Status' caption='Status'>
        <Lookup dataSource={WORK_TASK_STATUS_LIST} />
        <RequiredRule message='Selecteer een bewerking!' />
    </Column>
    <Column dataField='HasUsedMaterials' caption='Materiaal' />
    <Column
      dataField='Employees'
      caption='Toegewezen aan'
      width={200}
      allowSorting={false}
      editCellComponent={EmployeeTagBoxComponent}
      cellTemplate={cellTemplate}>
        <Lookup
          dataSource={employeeData}
          valueExpr='Oid'
          displayExpr='FullName' />
       <RequiredRule message='Selecteer een medeweerker!' />
    </Column>
    <Column dataField='StartDateGroup' dataType='datetime' format='dd-MM-yyyy HH:mm' sortOrder='desc' caption='Opdracht datum' groupIndex={0} calculateGroupValue={groupByDate} groupCellRender={groupCellRender} />
    <Column defaultSortOrder='asc' dataField='TaskStartDate' dataType='datetime' format='dd-MM-yyyy HH:mm' caption='Opdracht datum'>
       <RequiredRule message='Uitvoer datum mag niet leeg zijn!' />
    </Column>
    <Column dataField='TaskEndDate' dataType='datetime' format='dd-MM-yyyy HH:mm' caption='Tot datum' />
    <Column type='buttons' width={110}>
      <Button name='edit' />
      <Button name='delete' />
      <Button onClick={onExportToErp} icon='export' />
    </Column>
  </DataGrid>
  );
};

const GridWithLoadPanel = withLoadPanel(Grid);
export const WorkTaskTable = ({ relationOid, isLoading, backTo }: { relationOid?: string, isLoading: boolean, backTo?: string }) => {
  return (
    <div className='card-tasks'>
          <GridWithLoadPanel
        relationOid={relationOid}
        backTo={backTo}
        hasData
        loading={isLoading}
        panelProps={{
            container: '.card-tasks',
            position: { of: '.card-tasks' }
        }}
      />
    </div>
  );
};
